import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackParamList, RootStackNavigationProp } from '../../../../types/navigation';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('CasketUrnInputOther');

import {
  OtherDetails,
  CasketUrnDetails,
  casketUrnInfoService
} from '../../../../services/casketUrnInfo';

type CasketUrnInputOtherScreenRouteProp = RouteProp<RootStackParamList, 'CasketUrnInputOther'>;

export const CasketUrnInputOtherScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<CasketUrnInputOtherScreenRouteProp>();

  const { casketUrnData, isEditing } = route.params || {};

  const [specialInstructions, setSpecialInstructions] = useState(
    casketUrnData?.specialInstructions || ''
  );

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (casketUrnData?.details && casketUrnData.containerType === 'other') {
      return casketUrnData.details as OtherDetails;
    }
    return {};
  };

  const initialDetails = getInitialDetails();

  // Other Details state
  const [otherDetails, setOtherDetails] = useState<OtherDetails>({
    arrangementType: initialDetails?.arrangementType || '',
    description: initialDetails?.description || '',
    specifications: initialDetails?.specifications || '',
    location: initialDetails?.location || '',
    requirements: initialDetails?.requirements || '',
  });

  const handleSubmit = async () => {
    try {
      // Prepare the data to save
      const casketUrnUpdateData = {
        containerType: 'other',
        specificType: 'custom',
        specialInstructions: specialInstructions,
        details: otherDetails,
        notes: specialInstructions, // Map specialInstructions to notes field
      };

      let savedCasketUrn;

      if (isEditing && casketUrnData?.id) {
        // Update existing record
        savedCasketUrn = await casketUrnInfoService.updateCasketUrnInfo(
          casketUrnData.id,
          casketUrnUpdateData
        );
        logger.debug('Other arrangement information updated successfully with ID:', savedCasketUrn.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        (navigation as any).navigate('CasketUrnLockUnlock', { casketUrnId: savedCasketUrn.id });
      } else {
        // Create new record
        savedCasketUrn = await casketUrnInfoService.createCasketUrnInfo(casketUrnUpdateData);
        logger.debug('Other arrangement information saved successfully with ID:', savedCasketUrn.id);

        // Navigate to lock/unlock screen with the ID
        (navigation as any).navigate('CasketUrnLockUnlock', { casketUrnId: savedCasketUrn.id });
      }
    } catch (error) {
      logger.error('Error saving other arrangement information:', error);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Add Other Requests</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Type of alternative arrangement:</Text>
            <TextInput
              style={styles.structuredInput}
              value={otherDetails.arrangementType}
              onChangeText={(text) => setOtherDetails({ ...otherDetails, arrangementType: text })}
              placeholder="Natural burial, aquamation, body donation, etc..."
              multiline={true}
              textAlignVertical="top"
            />

            <Text style={styles.inputLabel}>Detailed description:</Text>
            <TextInput
              style={[styles.structuredInput, { minHeight: 120 }]}
              value={otherDetails.description}
              onChangeText={(text) => setOtherDetails({ ...otherDetails, description: text })}
              placeholder="Provide a detailed description of your preferred arrangement..."
              multiline={true}
              textAlignVertical="top"
            />

            <Text style={styles.inputLabel}>Location preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              value={otherDetails.location}
              onChangeText={(text) => setOtherDetails({ ...otherDetails, location: text })}
              placeholder="Specify any location requirements or preferences..."
              multiline={true}
              textAlignVertical="top"
            />

            <Text style={styles.inputLabel}>Special requirements:</Text>
            <TextInput
              style={styles.structuredInput}
              value={otherDetails.requirements}
              onChangeText={(text) => setOtherDetails({ ...otherDetails, requirements: text })}
              placeholder="Any special requirements, certifications, or considerations..."
              multiline={true}
              textAlignVertical="top"
            />

            <Text style={styles.inputLabel}>Technical specifications:</Text>
            <TextInput
              style={styles.structuredInput}
              value={otherDetails.specifications}
              onChangeText={(text) => setOtherDetails({ ...otherDetails, specifications: text })}
              placeholder="Technical details, procedures, or specific instructions..."
              multiline={true}
              textAlignVertical="top"
            />

            <Text style={styles.label}>Additional special instructions:</Text>
            <TextInput
              style={[styles.specialInstructionsInput, { height: 120 }]}
              value={specialInstructions}
              onChangeText={setSpecialInstructions}
              placeholder="Enter any additional special instructions..."
              multiline={true}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.submitButton}
              onPress={handleSubmit}
            >
              <Text style={styles.submitButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(100),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  inputLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(5),
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(15),
    minHeight: s(80),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(100),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  textArea: {
    minHeight: s(200),
    height: 'auto',
    textAlignVertical: 'top',
    paddingTop: ms(15),
    paddingHorizontal: ms(15),
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});

export default CasketUrnInputOtherScreen;
